import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { UploadCloud, Download, Trash2, Paperclip } from 'lucide-react';
import { fileSize, formatRelative } from '@/lib/format';
import { toast } from 'sonner';
import api from '@/lib/api';

const MAX_FILE_BYTES = 10 * 1024 * 1024;

export default function AttachmentsTab({ ticketId, attachments, onRefresh, onAfterUpload }) {
  const fileInput = useRef(null);

  const upload = async (files) => {
    if (!files || !files.length) return;
    for (const f of Array.from(files)) {
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name}: excede 10MB`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', f);
      try {
        await api.post(`/tickets/${ticketId}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(`${f.name} enviado`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Upload failed for ${f.name}:`, err);
        toast.error(`Falha em ${f.name}`);
      }
    }
    onRefresh && onRefresh();
    onAfterUpload && onAfterUpload();
  };

  const onDrop = (e) => {
    e.preventDefault();
    upload(e.dataTransfer.files);
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/tickets/attachments/${att.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.originalName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to download attachment:', err);
      toast.error('Erro ao baixar anexo');
    }
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Excluir ${att.originalName}?`)) return;
    try {
      await api.delete(`/tickets/attachments/${att.id}`);
      toast.success('Anexo excluído');
      onRefresh && onRefresh();
      onAfterUpload && onAfterUpload();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete attachment:', err);
      toast.error('Erro ao excluir anexo');
    }
  };

  return (
    <Card className="border-slate-200 p-5">
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click(); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-blue-400 transition cursor-pointer"
        onClick={() => fileInput.current?.click()}
      >
        <UploadCloud className="w-8 h-8 mx-auto text-slate-400" />
        <p className="mt-2 text-sm text-slate-700 font-medium">Clique ou arraste arquivos para fazer upload</p>
        <p className="text-xs text-slate-500">Limite de 10MB por arquivo</p>
        <input ref={fileInput} type="file" className="hidden" multiple onChange={(e) => upload(e.target.files)} />
      </div>
      <div className="mt-5">
        {!attachments || attachments.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">Nenhum anexo</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {attachments.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500">
                  <Paperclip className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{a.originalName}</div>
                  <div className="text-xs text-slate-500">
                    {fileSize(a.size)} • {formatRelative(a.createdAt)} • {a.uploadedByName || ''}
                  </div>
                </div>
                <Button variant="ghost" size="icon" aria-label="Baixar" onClick={() => downloadAttachment(a)}>
                  <Download className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" aria-label="Excluir" onClick={() => deleteAttachment(a)} className="text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
